import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { PurchasingNote } from "./PurchasingNoteCell";
import { PurchasingNotesByItemId } from "./usePurchasingNotesByItemId";
import { getPurchasingNoteQueryKey } from "./purchasing-note-query-key";

/**
 * Parameters for updating a purchasing note.
 */
interface UpdatePurchasingNoteParams {
  /** The existing note, or null if creating a new note */
  note: PurchasingNote | null;
  /** The new value for the note (empty string to delete) */
  newValue: string;
  /** The ID of the item this note is associated with */
  itemId: number;
  /** The ID of the job this note is associated with */
  jobId: number;
}

/**
 * Result of updating a purchasing note.
 */
interface UpdatePurchasingNoteResult {
  /** Whether the note was actually updated/created/deleted */
  updated: boolean;
  /** The updated note, null if deleted, or the unchanged existing note */
  newNote: PurchasingNote | null;
}

/**
 * Update the purchasing note for a specific item/job combination with the provided value.
 *
 * @param params - The parameters for updating the note
 * @returns The updated note if the value has changed, null if the note was deleted, or false if no changes were made.
 */
async function updatePurchasingNote({
  note,
  newValue,
  itemId,
  jobId,
}: UpdatePurchasingNoteParams): Promise<UpdatePurchasingNoteResult> {
  if (!newValue) {
    // If the input is empty and there is no existing note, do nothing
    if (!note) return { updated: false, newNote: note };

    // If the input is empty and there is an existing note, delete it
    await supabase.from("PurchasingNote").delete().eq("itemId", note.itemId).eq("jobId", note.jobId).throwOnError();
    return { updated: true, newNote: null };
  }

  // If there is no existing note, create a new one
  if (!note) {
    const newNote = (
      await supabase.from("PurchasingNote").insert({ itemId, jobId, value: newValue }).select().single().throwOnError()
    ).data;
    return { updated: true, newNote };
  }

  // If the text has changed, update the existing note
  if (note.value !== newValue) {
    const newNote = (
      await supabase
        .from("PurchasingNote")
        .update({ value: newValue })
        .eq("itemId", itemId)
        .eq("jobId", jobId)
        .select()
        .single()
        .throwOnError()
    ).data;
    return { updated: true, newNote };
  }

  // Otherwise, the note is unchanged, return the existing note
  return { updated: false, newNote: note };
}

/**
 * Hook to update a purchasing note and automatically update the cached query data.
 *
 * This hook uses TanStack Query's mutation to handle the update operation and automatically
 * updates the cache so that all components using the purchasing notes query will immediately
 * see the changes without needing to refetch.
 *
 * @returns A mutation object with `mutate` and `mutateAsync` functions to trigger the update
 */
export function useUpdatePurchasingNoteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePurchasingNote,
    onSuccess: (result, variables) => {
      // Only update cache if something actually changed
      if (!result.updated) return;

      const { jobId, itemId } = variables;
      const queryKey = getPurchasingNoteQueryKey(jobId);

      // Update the cached data immediately
      queryClient.setQueryData<PurchasingNotesByItemId>(queryKey, (oldData) => {
        if (!oldData) return oldData;

        const newData = new Map(oldData);

        if (result.newNote === null) {
          // Note was deleted
          newData.delete(itemId);
        } else {
          // Note was created or updated
          newData.set(itemId, result.newNote);
        }

        return newData;
      });
    },
  });
}
